<template>
  <div ref="containerRef" class="w-full relative">
    <label v-if="label" :for="name" class="block mb-1 text-sm font-medium text-white">
      {{ label }} <span v-if="required && !disabled" class="text-red-500">*</span>
    </label>

    <div class="relative">
      <input 
        @keydown="onKeyDown" 
        :id="name" 
        :name="name" 
        :type="showPassword ? 'text' : 'password'"
        v-model="internalValue" 
        :placeholder="placeholder" 
        :class="[
          baseClass,
          error ? 'border-red-500' : 'border-gray-600',
          customClass
        ]" 
        :disabled="disabled" 
        @focus="onFocus"
        @blur="onBlur" 
        @input="onInputPassword" 
        :maxlength="length" 
      />

      <!-- Eye toggle icon -->
      <button 
        v-if="!disabled"
        type="button" 
        class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
        @click="togglePassword" 
        tabindex="-1"
      >
        <svg v-if="showPassword" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7a9.984 9.984 0 012.398-4.368M6.343 6.343A9.964 9.964 0 0112 5c4.477 0 8.268 2.943 9.542 7a9.963 9.963 0 01-4.072 5.293M15 12a3 3 0 00-3-3m0 0a3 3 0 00-3 3m6 0a3 3 0 01-3 3m0 0a3 3 0 01-3-3m-6 6l18-18" />
        </svg>
      </button>
    </div>

    <transition name="fade-slide">
      <div 
        v-if="!disabled && internalValue && !allMet && isFocused" 
        class="absolute z-[100] left-0 right-0 mt-1 p-3 bg-white border border-gray-200 rounded-lg shadow-xl space-y-3 pointer-events-none mobile:max-w-[calc(100vw-2rem)] mobile:mx-auto"
      >
        <!-- Strength Bar -->
        <div class="space-y-1">
          <div class="flex items-center justify-between">
            <span class="text-[10px] font-bold uppercase tracking-wider text-gray-400">Strength: {{ strengthLabel }}</span>
          </div>
          <div class="flex gap-1 h-1">
            <div 
              v-for="i in 4" 
              :key="i"
              class="flex-1 rounded-full transition-all duration-500"
              :class="i <= strengthScore ? strengthColor : 'bg-gray-100'"
            ></div>
          </div>
        </div>

        <!-- Requirements List -->
        <div class="space-y-2">
          <div v-for="(requirement, index) in requirements" :key="index" class="flex items-center gap-2">
            <div 
              class="w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center transition-all duration-300"
              :class="requirement.met ? 'bg-green-500' : 'bg-red-500'"
            >
              <svg v-if="requirement.met" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-white" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
              <div v-else class="w-1 h-1 bg-white rounded-full"></div>
            </div>
            <span 
              class="text-xs transition-all duration-300"
              :class="requirement.met ? 'text-green-500' : 'text-red-500'"
            >
              {{ requirement.text }}
            </span>
          </div>
        </div>
      </div>
    </transition>

    <p v-if="error && !disabled" :class="[
      'text-xs mt-1 mb-0',
      errorClass ? errorClass : 'text-customYellow-100'
    ]" class="text-xs mt-1 mb-0">{{ error }}</p>
  </div>
</template>

<script setup>
import { ref, watch, computed, defineExpose } from 'vue';

const props = defineProps({
  modelValue: {},
  name: String,
  label: String,
  placeholder: String,
  required: {
    type: Boolean,
    default: false
  },
  regex: RegExp,
  errorMessage: String,
  customClass: {
    type: String,
    default: ''
  },
  errorClass: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    default: false
  },
  confirmWith: {
    type: String,
    default: ''
  },
  length: {
    type: String,
    default: null
  }
});

const emit = defineEmits(['update:modelValue', 'input']);

const internalValue = ref(props.modelValue || '');
const error = ref('');
const touched = ref(false);
const showPassword = ref(false);
const isFocused = ref(false);
const containerRef = ref(null);

const baseClass = 'w-full mobile:p-1.5 largeDesktop:p-2 pr-10 mobile:rounded-md largeDesktop:rounded-lg border outline-none text-sm !text-[#333] disabled:cursor-not-allowed disabled:!bg-[#EFEFEF] disabled:!text-[#666] !appearance-none';

const requirements = computed(() => {
  const rawVal = internalValue.value || '';
  const val = rawVal.replace(/\s/g, '');
  const reqs = [
    { text: 'Minimum 8 characters', met: val.length >= 8 },
    { text: 'At least one uppercase letter', met: /[A-Z]/.test(val) },
    { text: 'At least one number', met: /[0-9]/.test(val) },
    { text: 'At least one symbol (!@#$%^&*)', met: /[!@#$%^&*(),.?":{}|<>[\];'`~+=_-]/.test(val) }
  ];

  if (/\s/.test(rawVal)) {
    reqs.push({ text: 'No spaces allowed', met: false });
  }

  return reqs;
});

const allMet = computed(() => requirements.value.every(r => r.met));

const strengthScore = computed(() => requirements.value.filter(r => r.met).length);

const strengthLabel = computed(() => {
  if (strengthScore.value === 0) return 'Weak';
  if (strengthScore.value === 1) return 'Weak';
  if (strengthScore.value === 2) return 'Fair';
  if (strengthScore.value === 3) return 'Good';
  return 'Strong';
});

const strengthColor = computed(() => {
  if (strengthScore.value <= 1) return 'bg-red-500';
  if (strengthScore.value === 2) return 'bg-yellow-500';
  if (strengthScore.value === 3) return 'bg-blue-500';
  return 'bg-green-500';
});

watch(() => props.modelValue, (val) => {
  internalValue.value = val || '';
});

watch(internalValue, (val) => {
  emit('update:modelValue', val);
  if (touched.value && !props.disabled) {
    validate();
  }
});

watch(() => props.confirmWith, () => {
  if (touched.value && !props.disabled) {
    validate();
  }
});

function onFocus() {
  isFocused.value = true;
  // Ensure field is visible on mobile when keyboard opens
  setTimeout(() => {
    containerRef.value?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, 300);
}

function onBlur() {
  isFocused.value = false;
  touched.value = true;
  validate();
}

function onKeyDown(e) {
  if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
    e.preventDefault();
  }

  if (props.length &&
    e.target.value.length >= props.length &&
    e.key.length === 1 && 
    !e.ctrlKey && !e.metaKey
  ) {
    e.preventDefault();
  }
}

function onInputPassword(e) {
  let val = e.target.value;
  // Block emojis
  val = val.replace(/[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu, '');
  internalValue.value = val;
  emit("input", val);
  validate();
}

function togglePassword() {
  showPassword.value = !showPassword.value;
}

function validate() {
  if (props.disabled) return '';
  const val = internalValue.value || '';

  if (props.required && val.trim() === '') {
    error.value = 'This field is required.';
    return error.value;
  }

  if (val !== '' && props.regex && !props.regex.test(val)) {
    error.value = props.errorMessage || 'Invalid password format.';
    return error.value;
  }

  if (props.confirmWith !== undefined && props.confirmWith !== null && props.confirmWith !== '') {
    if (props.confirmWith !== val) {
      error.value = props.errorMessage || 'Passwords do not match.';
      return error.value;
    }
  }

  error.value = '';
  return '';
}

function resetField() {
  error.value = ''
}

defineExpose({
  resetField,
  validate,
  hasError: () => !!error.value
});
</script>

<style scoped>
input[type="password"]::-ms-reveal,
input[type="password"]::-ms-clear {
  display: none;
}

/* Custom transitions for a premium feel */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
